/**
 * DATA LAYER (DL): CARRERA CONTINUA
 * Se comunica con los endpoints REST del backend para Carrera Continua.
 */

const getApiBaseUrl = () => {
    const raw = process.env.REACT_APP_API_URL || 'https://localhost:50586/api/';
    return raw.endsWith('/') ? `${raw}carreracontinua` : `${raw}/carreracontinua`;
};

/**
 * Obtiene los temas de Carrera Continua disponibles.
 */
export const GetThemesDL = async (roomId, operatorId) => {
    try {
        const query = new URLSearchParams();
        if (roomId) query.append('roomId', roomId);
        if (operatorId) query.append('operatorId', operatorId);

        const url = `${getApiBaseUrl()}/themes${query.toString() ? `?${query.toString()}` : ''}`;
        const response = await fetch(url, {
            method: 'GET',
            headers: { 'Content-Type': 'application/json' }
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            return { success: false, message: errorData.message || 'Error al obtener los temas.' };
        }

        const data = await response.json();
        return { success: true, data: data.data || data };
    } catch (error) {
        return { success: false, message: 'Error de red al obtener temas.' };
    }
};

/**
 * Obtiene el detalle de un tema específico por su ID.
 */
export const GetThemeByIdDL = async (id) => {
    try {
        const response = await fetch(`${getApiBaseUrl()}/theme/${id}`, {
            method: 'GET',
            headers: { 'Content-Type': 'application/json' }
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            return { success: false, message: errorData.message || 'Error al obtener el tema.' };
        }

        const data = await response.json();
        return { success: true, data: data.data || data };
    } catch (error) {
        return { success: false, message: 'Error de red al obtener el tema.' };
    }
};

/**
 * Crea un nuevo tema personalizado de Carrera Continua.
 */
export const CreateThemeDL = async (dataPayload) => {
    try {
        const response = await fetch(`${getApiBaseUrl()}/theme`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(dataPayload)
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            return { success: false, message: errorData.message || 'Error al crear el tema.' };
        }

        const data = await response.json();
        return { success: true, data: data.data || data };
    } catch (error) {
        return { success: false, message: 'Error de red al crear el tema.' };
    }
};

/**
 * Actualiza un tema existente.
 */
export const UpdateThemeDL = async (id, dataPayload) => {
    try {
        const response = await fetch(`${getApiBaseUrl()}/theme/${id}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(dataPayload)
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            return { success: false, message: errorData.message || 'Error al actualizar el tema.' };
        }

        const data = await response.json();
        return { success: true, data: data.data || data };
    } catch (error) {
        return { success: false, message: 'Error de red al actualizar el tema.' };
    }
};

/**
 * Elimina un tema personalizado.
 */
export const DeleteThemeDL = async (id) => {
    try {
        const response = await fetch(`${getApiBaseUrl()}/theme/${id}`, {
            method: 'DELETE',
            headers: { 'Content-Type': 'application/json' }
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            return { success: false, message: errorData.message || 'Error al eliminar el tema.' };
        }

        const data = await response.json();
        return { success: true, message: data.message || 'Tema eliminado exitosamente.' };
    } catch (error) {
        return { success: false, message: 'Error de red al eliminar el tema.' };
    }
};

/**
 * Clona un tema existente como una nueva copia editable.
 */
export const CloneThemeDL = async (id, newName) => {
    try {
        const response = await fetch(`${getApiBaseUrl()}/theme/${id}/clone`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ newName })
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            return { success: false, message: errorData.message || 'Error al clonar el tema.' };
        }

        const data = await response.json();
        return { success: true, data: data.data || data, message: data.message };
    } catch (error) {
        return { success: false, message: 'Error de red al clonar el tema.' };
    }
};

/**
 * Sube y optimiza un recurso gráfico para Carrera Continua.
 */
export const UploadAssetDL = async (formData) => {
    try {
        const response = await fetch(`${getApiBaseUrl()}/upload`, {
            method: 'POST',
            body: formData
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            return { success: false, message: errorData.message || 'Error al subir el archivo.' };
        }

        const data = await response.json();
        return { success: true, data };
    } catch (error) {
        return { success: false, message: 'Error de red al subir el archivo.' };
    }
};

/**
 * Obtiene el historial de partidas disputadas en una sala.
 */
export const GetMatchHistoryDL = async (roomId, limit = 20) => {
    try {
        const response = await fetch(`${getApiBaseUrl()}/history/${roomId}?limit=${limit}`, {
            method: 'GET',
            headers: { 'Content-Type': 'application/json' }
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            return { success: false, message: errorData.message || 'Error al obtener el historial de partidas.' };
        }

        const data = await response.json();
        return { success: true, data: data.data || data };
    } catch (error) {
        return { success: false, message: 'Error de red al obtener el historial.' };
    }
};

/**
 * Registra y persiste el resultado final de una partida.
 */
export const SaveMatchResultDL = async (dataPayload) => {
    try {
        const response = await fetch(`${getApiBaseUrl()}/history`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(dataPayload)
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            return { success: false, message: errorData.message || 'Error al guardar el resultado.' };
        }

        const data = await response.json();
        return { success: true, data: data.data || data };
    } catch (error) {
        return { success: false, message: 'Error de red al guardar el resultado.' };
    }
};

/**
 * Inicializa una nueva partida en tiempo real en estado 'Waiting'.
 */
export const CreateLiveMatchDL = async (dataPayload) => {
    try {
        const response = await fetch(`${getApiBaseUrl()}/realtime/create`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(dataPayload)
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            return { success: false, message: errorData.message || 'Error al crear la partida en tiempo real.' };
        }

        const data = await response.json();
        return { success: true, data: data.data || data };
    } catch (error) {
        return { success: false, message: 'Error de red al crear la partida.' };
    }
};

/**
 * Inicia la cuenta regresiva sincronizada (3, 2, 1) y arranque de carrera.
 */
export const StartLiveMatchDL = async (dataPayload) => {
    try {
        const response = await fetch(`${getApiBaseUrl()}/realtime/start`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(dataPayload)
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            return { 
                success: false, 
                message: errorData.message || 'Error al iniciar la carrera.',
                error: errorData.error 
            };
        }

        const data = await response.json();
        return { success: true, data: data.data || data, message: data.message };
    } catch (error) {
        return { success: false, message: 'Error de red al iniciar la carrera.' };
    }
};

/**
 * Pausa temporalmente una carrera en curso.
 */
export const PauseLiveMatchDL = async (dataPayload) => {
    try {
        const response = await fetch(`${getApiBaseUrl()}/realtime/pause`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(dataPayload)
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            return { success: false, message: errorData.message || 'Error al pausar la carrera.' };
        }

        const data = await response.json();
        return { success: true, message: data.message || 'Carrera pausada.' };
    } catch (error) {
        return { success: false, message: 'Error de red al pausar la carrera.' };
    }
};

/**
 * Reanuda una carrera previamente pausada.
 */
export const ResumeLiveMatchDL = async (dataPayload) => {
    try {
        const response = await fetch(`${getApiBaseUrl()}/realtime/resume`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(dataPayload)
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            return { success: false, message: errorData.message || 'Error al reanudar la carrera.' };
        }

        const data = await response.json();
        return { success: true, message: data.message || 'Carrera reanudada.' };
    } catch (error) {
        return { success: false, message: 'Error de red al reanudar la carrera.' };
    }
};

/**
 * Finaliza forzadamente una carrera calculando resultados y persistiendo.
 */
export const FinishLiveMatchEarlyDL = async (dataPayload) => {
    try {
        const response = await fetch(`${getApiBaseUrl()}/realtime/finish`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(dataPayload)
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            return { success: false, message: errorData.message || 'Error al finalizar la carrera.' };
        }

        const data = await response.json();
        return { success: true, message: data.message || 'Carrera finalizada.' };
    } catch (error) {
        return { success: false, message: 'Error de red al finalizar la carrera.' };
    }
};

/**
 * Reinicia la carrera de una sala devolviéndola al estado 'Waiting'.
 */
export const ResetLiveMatchDL = async (dataPayload) => {
    try {
        const response = await fetch(`${getApiBaseUrl()}/realtime/reset`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(dataPayload)
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            return { success: false, message: errorData.message || 'Error al reiniciar la carrera.' };
        }

        const data = await response.json();
        return { success: true, message: data.message || 'Carrera reiniciada.' };
    } catch (error) {
        return { success: false, message: 'Error de red al reiniciar la carrera.' };
    }
};

/**
 * Obtiene el estado actual en memoria de la partida de una sala.
 */
export const GetLiveMatchStateDL = async (roomId) => {
    try {
        const response = await fetch(`${getApiBaseUrl()}/realtime/state/${roomId}`, {
            method: 'GET',
            headers: { 'Content-Type': 'application/json' }
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            return { success: false, message: errorData.message || 'Error al obtener estado de la partida.' };
        }

        const data = await response.json();
        return { success: true, data: data.data || data };
    } catch (error) {
        return { success: false, message: 'Error de red al obtener estado.' };
    }
};

/**
 * Registra y envía taps acumulados para una zona en la carrera activa.
 */
export const AddLiveTapsDL = async ({ roomId, zoneIdentifier, tapsCount }) => {
    try {
        const response = await fetch(`${getApiBaseUrl()}/realtime/tap`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                roomId,
                zoneIdentifier,
                tapsCount: Number(tapsCount) || 1
            })
        });

        if (!response.ok) {
            return { success: false };
        }

        const data = await response.json().catch(() => ({ success: true }));
        return { success: true, data };
    } catch (error) {
        return { success: false, error };
    }
};

/**
 * Proyecta la portada de Carrera Continua hacia la pantalla gigante (/screen y /screenv2).
 */
export const ShowCarreraContinuaCoverDL = async (roomId, themeId) => {
    try {
        const queryParams = new URLSearchParams();
        if (roomId) queryParams.append('roomId', roomId);
        if (themeId) queryParams.append('themeId', themeId);

        const response = await fetch(`${getApiBaseUrl()}/realtime/show-cover?${queryParams.toString()}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' }
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            return { success: false, message: errorData.message || 'Error al proyectar la portada.' };
        }

        const data = await response.json();
        return { success: true, data };
    } catch (error) {
        return { success: false, message: 'Error de red al proyectar la portada.' };
    }
};

/**
 * Proyecta la pantalla de juego (lienzo de atletismo) hacia la pantalla gigante (/screen y /screenv2).
 */
export const ShowCarreraContinuaGameDL = async (roomId, themeId) => {
    try {
        const queryParams = new URLSearchParams();
        if (roomId) queryParams.append('roomId', roomId);
        if (themeId) queryParams.append('themeId', themeId);

        const response = await fetch(`${getApiBaseUrl()}/realtime/project-game?${queryParams.toString()}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' }
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            return { success: false, message: errorData.message || 'Error al proyectar el juego.' };
        }

        const data = await response.json();
        return { success: true, data };
    } catch (error) {
        return { success: false, message: 'Error de red al proyectar el juego.' };
    }
};

